<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDateField,
  HrDismissBtn,
  HrFormDialog,
  HrListSelect,
  HrSettingCheckbox,
  HrTextField,
} from 'src/design-system';
import { computed, ref } from 'vue';

import { DEGREE_LEVELS } from '../../domain/constants/degree';

/**
 * Records a degree somebody was awarded.
 *
 * NO END DATE, AND THAT IS THE SHAPE. A degree is conferred once and never lapses, so it carries one
 * date. Anything that expires is a certification and belongs in the other dialog.
 *
 * ACCREDITATION IS A CHECKBOX BECAUSE IT IS A GATE. An unaccredited degree counts as one the person
 * does not hold, since payer rules read "from an accredited institution", so the hint says that
 * rather than leaving somebody to guess why the box matters.
 *
 * THE FIELD OF STUDY IS NOT OPTIONAL POLISH. Some payers grant the bachelor's-level modifier only
 * for a bachelor's in a related field and others grant it for any bachelor's, so the field is
 * recorded on every degree even though only some rules read it. Which fields count as related is the
 * rule's business, not this form's.
 *
 * FOREIGN DEGREES ARE NOT ASKED ABOUT HERE. They count only through an equivalency evaluation
 * stating the US level, and that evaluation is a document with an issuer and a date of its own. The
 * model carries it; recording one is not in this bet, so the field is absent rather than a
 * half-question somebody would answer wrongly.
 */
defineOptions({ name: 'EditDegreeDialog' });

// No props: the degree being edited arrives through `show()`, so a stale one cannot sit bound to the
// dialog between openings.
const emit = defineEmits({ ok: null });

const dialog = ref(null);
const draft = ref(null);

const blank = () => ({
  level: null,
  fieldOfStudy: null,
  school: null,
  conferredOn: null,
  isAccredited: true,
  // Present and null, matching what a degree read from the document carries. A missing key is not
  // the same as an empty value to Firestore, which refuses `undefined` and accepts null.
  equivalency: null,
  proof: null,
});

const levelOptions = Object.values(DEGREE_LEVELS)
  .map((level) => ({ value: level, label: i18n.t(`staff.qualifications.levels.${level}`) }));

// A degree with no rung answers nothing, and one with no conferral date cannot be read for a past
// service date. Both are what the record exists to say, so neither is optional.
const canConfirm = computed(() => {
  return draft.value?.level !== null && typeof draft.value?.conferredOn === 'string';
});

function show({ degree = null } = {}) {
  draft.value = degree === null ? blank() : { ...degree };
  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

function onSubmit() {
  if (!canConfirm.value) {
    return;
  }

  emit('ok', { ...draft.value });
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="i18n.t('staff.qualifications.titleDegreeDialog')"
    data-e2e="dialog-edit-staff-degree"
    @submit="onSubmit"
  >
    <div
      v-if="draft !== null"
      class="row q-col-gutter-md"
    >
      <hr-list-select
        v-model="draft.level"
        class="col-12"
        :options="levelOptions"
        :label="i18n.t('staff.qualifications.inputDegreeLevel')"
        data-e2e="select-staff-degree-level"
      />

      <hr-text-field
        v-model="draft.fieldOfStudy"
        class="col-12"
        :label="i18n.t('staff.qualifications.inputFieldOfStudy')"
        data-e2e="input-staff-degree-field"
      />

      <hr-text-field
        v-model="draft.school"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputSchool')"
        data-e2e="input-staff-degree-school"
      />

      <hr-date-field
        v-model="draft.conferredOn"
        class="col-12 col-sm-6"
        :label="i18n.t('staff.qualifications.inputConferredOn')"
        data-e2e="input-staff-degree-conferred"
      />

      <hr-setting-checkbox
        v-model="draft.isAccredited"
        class="col-12"
        :label="i18n.t('staff.qualifications.inputAccredited')"
        :hint="i18n.t('staff.qualifications.hintAccredited')"
        data-e2e="checkbox-staff-degree-accredited"
      />

      <hr-text-field
        v-model="draft.proof"
        class="col-12"
        :label="i18n.t('staff.qualifications.inputProof')"
        data-e2e="input-staff-degree-proof"
      />
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonSave')"
        type="submit"
        :disable="!canConfirm"
        data-e2e="btn-save-staff-degree"
      />
    </template>
  </hr-form-dialog>
</template>
